import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useClickAway, useHoverDirty, usePrevious } from 'react-use';
import InfoIcon from 'components/icons/IconInfoNew';
import CloseIcon from 'components/icons/iconClose';
import useOnClick from 'hooks/useOnClick';
import useScreen from 'hooks/useScreen';
import BrowserOnly from '../BrowserOnly';
const InfoTooltip = ({
  align = 'standard',
  arrowOffset = 0,
  children,
  className = '',
  closeButtonClassName,
  closeButtonDataXc,
  customIcon = null,
  direction = 'bottom',
  disableHoverOnContent = false,
  iconClassName = '',
  iconDataXc,
  iconLabel = '',
  iconSize = 'small',
  inactiveOnMobile = false,
  isOpenDefault = false,
  leftOffsetOverride = null,
  modifier = '',
  onTooltipToggle = null,
  openOnHover = true,
  parentRef,
  parentTitle = '',
  showAbove = false,
  showCloseButton = false,
  showInfoIcon = true,
  text = '',
  textColor = 'gray',
  variant,
  withArrow = false,
  hideIconOnOpenMobile = false
}) => {
  const [isOpen, setIsOpen] = useState(isOpenDefault);
  const [tooltipXOffset, setTooltipXOffset] = useState(0);
  const [xOffsetApplied, setXOffsetApplied] = useState(false);
  const {
    isMobile,
    isMobileOrTablet
  } = useScreen();
  const isMobileMode = isMobileOrTablet && inactiveOnMobile;
  const openTooltip = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeTooltip = useCallback(() => {
    setIsOpen(false);
  }, []);
  const previousIsOpen = usePrevious(isOpen);
  const tooltipContentRef = useRef(null);

  // If the tooltip is opened on mobile, close it after 5 seconds
  useEffect(() => {
    if (isMobile && !previousIsOpen && isOpen) {
      setTimeout(() => {
        closeTooltip();
      }, 5000);
    }
  }, [isMobile, previousIsOpen, isOpen, closeTooltip]);
  const [closeClicked, setCloseClicked] = useState(false);
  useEffect(() => {
    if (onTooltipToggle) {
      onTooltipToggle(isOpen, parentTitle);
    }
  }, [isOpen, onTooltipToggle, parentTitle]);
  const tooltipRef = useRef(null);

  // If parentRef is defined, use it for detecting hover & click events instead
  // of the info icon
  const hoverRef = parentRef || tooltipRef;
  useOnClick(hoverRef, openTooltip);
  useClickAway(hoverRef, closeTooltip);
  const isUserHoveringOverTooltip = useHoverDirty(hoverRef);
  useEffect(() => {
    if (tooltipContentRef.current && openOnHover) {
      if (isUserHoveringOverTooltip && !closeClicked) {
        openTooltip();
      } else if (!isMobileOrTablet) {
        closeTooltip();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeClicked, isMobileOrTablet, isUserHoveringOverTooltip, openOnHover]);
  const isBrandStyle = modifier === 'brand';
  const isBrandFadeStyle = modifier === 'brand-fade';
  const paddingOffset = isMobileMode ? 8 : 16; // extra padding from screen edges

  useEffect(() => {
    if (!isOpen || !tooltipContentRef.current || xOffsetApplied) return;
    const {
      left,
      right
    } = tooltipContentRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const contentWidth = right - left;
    // Prefer leftOffsetOverride when set (Greenguard PDP sits at the sidebar's
    // left edge — default centering would clip under content-visibility).
    // Otherwise preferredOffset always shifts the tooltip towards the left.
    const preferredOffset = leftOffsetOverride !== null ? leftOffsetOverride : align === 'left' ? contentWidth * -0.25 : contentWidth * -0.5;

    // Clamp offset to prevent horizontal scroll
    const maxRightOffset = windowWidth - right - paddingOffset;
    const maxLeftOffset = -(left - paddingOffset);
    const offset = Math.max(maxLeftOffset, Math.min(maxRightOffset, preferredOffset));
    setTooltipXOffset(offset);
    setXOffsetApplied(true);
  }, [align, isOpen, leftOffsetOverride, paddingOffset, xOffsetApplied]);
  const handleWindowSizeChange = useCallback(() => {
    setXOffsetApplied(false);
  }, []);
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);
  return <div className={classNames('inline-block relative', {
    '!ml-[3px] [&_svg]:!w-2.5 [&_svg]:!h-2.5': isBrandStyle && isBrandFadeStyle,
    '[&_svg]:!mx-1 [&_svg]:!my-0': isBrandFadeStyle,
    '[&_svg]:!w-3.5 [&_svg]:!h-3.5 [&_svg]:mx-2 [&_svg]:my-0 [&_svg]:[filter:invert(22%)__sepia(4%)__saturate(0)__hue-rotate(55deg)__brightness(104%)__contrast(91%)]': iconSize === 'big',
    'ml-0': !!text,
    // ModularItem variant
    '!absolute !right-[3px] !top-[3px] !z-[2000]': variant === 'ModularItem',
    // PDPShownInUI variant
    '!ml-0': variant === 'PDPShownInUI'
  }, className)} ref={tooltipRef}>
      {!!text && <span className={classNames({
      'text-brand': textColor === 'brand',
      'text-gray': textColor === 'gray',
      hidden: isOpen && isMobileMode && hideIconOnOpenMobile
    })}>
          {text}
        </span>}
      {!!showInfoIcon && !customIcon && !iconLabel && <InfoIcon dataXc={iconDataXc} className={classNames(`cursor-pointer h-3.5 relative top-px w-3.5 [&_g]:fill-brand  ${iconClassName}`, {
      hidden: isOpen && isMobileMode && hideIconOnOpenMobile
    })} onClick={() => {
      openTooltip();
    }} />}
      {/* No onClick: the wrapper's click listener also catches Enter and Space */}
      {!!showInfoIcon && !customIcon && !!iconLabel && <button aria-expanded={isOpen} aria-label={iconLabel} data-xc={iconDataXc} className={classNames('block p-0 m-0 bg-transparent border-0 rounded-full cursor-pointer focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand', {
      hidden: isOpen && isMobileMode && hideIconOnOpenMobile
    })} onKeyDown={event => {
      // The mobile notification belongs to the parent and has its own close button
      if (event.key === 'Escape' && !isMobileMode) {
        closeTooltip();
      }
    }} type="button">
          <InfoIcon aria-hidden="true" className={`h-3.5 relative top-px w-3.5 [&_g]:fill-brand ${iconClassName}`} />
        </button>}
      {/* customIcon will always override infoIcon */}
      {!!customIcon && <button type="button" className="inline" onClick={e => {
      e.stopPropagation();
      openTooltip();
    }}>
          {customIcon}
        </button>}
      {withArrow && !isMobileMode ? <div className={classNames('absolute border-t-transparent border-x-transparent border-b-brand-dark border-solid [border-width:10px_12px] left-0 top-3 w-[25px]', {
      hidden: !isOpen && !isBrandFadeStyle,
      '!top-[unset] !bottom-[11px] !rotate-180': direction === 'top',
      '!left-2/4 translate-x-[-50%]': arrowOffset === 0,
      '!left-10': variant === 'PDPShownInUI',
      'pointer-events-none': disableHoverOnContent,
      '[transition:opacity_.5s_ease-in-out]': isBrandFadeStyle,
      'invisible opacity-0': isBrandFadeStyle && !isOpen,
      'visible opacity-100': isBrandFadeStyle && isOpen,
      '!border-t-brand-dark !border-x-transparent !border-b-transparent !top-auto bottom-[5px]': showAbove
    })} style={{
      left: arrowOffset
    }} /> : null}

      {!isMobileMode && (isOpen || isBrandFadeStyle) && <BrowserOnly>
          <div ref={tooltipContentRef} className={classNames('absolute z-[1] left-0 w-[315px] max-w-[calc(100vw-32px)] h-fit top-5 rounded-sm bg-white border border-[#dbdbdb] p-6 whitespace-pre-wrap max-h-[80vh] overflow-auto ',
      // Hover buffer
      'before:lg:content-[""] before:lg:absolute before:lg:-top-5 before:lg:left-0 before:lg:right-0 before:lg:h-5 before:lg:cursor-pointer', {
        'top-auto bottom-[30px]': direction === 'top',
        'flex flex-row': variant === 'PDPCustomerReviews',
        'lg:-translate-x-1/4': variant === 'PDPShownInUI',
        'pointer-events-none': disableHoverOnContent,
        '[&_a]:no-underline [&_a]:text-white': isBrandStyle || isBrandFadeStyle,
        'bg-brand-dark text-white border-0 rounded-sm top-[29px]': isBrandStyle,
        'cursor-pointer bg-brand-dark bg-[url(https://joybird2.imgix.net/user-uploads-staging/joybird-teal-new.jpg?w=400&cs=srgb)] bg-cover text-white border-0 rounded-sm top-[29px] flex flex-row-reverse justify-between items-start z-[4000] transition-opacity duration-500 ease-in-out max-[1023px]:cursor-pointer': isBrandFadeStyle,
        '[&_button]:cursor-pointer [&_button]:bg-none [&_button]:border-0 [&_button]:float-right': isBrandFadeStyle,
        'invisible opacity-0 !w-0 !translate-x-0': isBrandFadeStyle && !isOpen,
        'visible opacity-100 translate-x-[var(--tooltip-x-offset)]': isBrandFadeStyle && isOpen,
        'translate-x-[var(--tooltip-x-offset)]': !isBrandFadeStyle,
        'bottom-[25px] top-auto': showAbove,
        'before:lg:-bottom-5 before:lg:top-auto': showAbove
      })} style={{
        '--tooltip-x-offset': `${tooltipXOffset}px`
      }}>
            {showCloseButton || !openOnHover && !isMobileOrTablet ? <div className={classNames('flex items-center mt-0 mx-0 mb-2 justify-between w-[15%] [&_svg_path]:fill-white', closeButtonClassName, {
          '!justify-end': parentTitle === '' && isBrandFadeStyle
        })}>
                {!!parentTitle && <h3 className="text-base font-bold mt-0 mb-0 ml-0 mr-2">
                    {parentTitle}
                  </h3>}
                <button onClick={event => {
            event.stopPropagation();
            setCloseClicked(true);
            closeTooltip();
          }} data-xc={closeButtonDataXc} type="button">
                  <CloseIcon />
                </button>
              </div> : null}
            {children}
          </div>
        </BrowserOnly>}
    </div>;
};
InfoTooltip.propTypes = {
  // align: PropTypes.oneOf(['standard', 'left']),
  arrowOffset: PropTypes.number,
  children: PropTypes.node,
  className: PropTypes.string,
  closeButtonClassName: PropTypes.string,
  closeButtonDataXc: PropTypes.string,
  customIcon: PropTypes.node,
  direction: PropTypes.oneOf(['bottom', 'top']),
  disableHoverOnContent: PropTypes.bool,
  iconClassName: PropTypes.string,
  iconDataXc: PropTypes.string,
  iconLabel: PropTypes.string,
  iconSize: PropTypes.string,
  inactiveOnMobile: PropTypes.bool,
  isOpenDefault: PropTypes.bool,
  leftOffsetOverride: PropTypes.number,
  modifier: PropTypes.oneOf(['', 'brand', 'brand-fade']),
  onTooltipToggle: PropTypes.func,
  openOnHover: PropTypes.bool,
  parentRef: PropTypes.object,
  parentTitle: PropTypes.string,
  showAbove: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  showInfoIcon: PropTypes.bool,
  text: PropTypes.string,
  textColor: PropTypes.oneOf(['brand', 'gray']),
  variant: PropTypes.oneOf(['ModularItem', 'PDPCustomerReviews', 'PDPShownInUI']),
  withArrow: PropTypes.bool
};
export default InfoTooltip;